import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import { AccountTree, ArrowBack, ArrowDropDown, Download, InsertDriveFile, Print } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import WorkflowDiagram, {
  DIAGRAM_END,
  diagramWorkflowId,
  diagramWorkflowTarget,
  WorkflowDiagramStep,
} from "../components/WorkflowDiagram";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  workflowCatalogService,
  workflowErrorMessage,
  downloadBlob,
  formatFileSize,
  WorkflowAttachment,
  WorkflowCatalogDetail,
  DUE_ANCHOR_LABELS,
  DUE_UNIT_LABELS,
  STEP_KIND_LABELS,
  TARGET_KIND_LABELS,
} from "../services/workflowsService";

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "-";

const WorkflowView: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const workflowId = getParam("workflowId") || "";

  const [printMenu, setPrintMenu] = useState<HTMLElement | null>(null);
  const [detail, setDetail] = useState<WorkflowCatalogDetail | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Marca no <html> o que deve sair no papel e imprime. A classe é retirada depois do print para a
   * tela voltar ao normal — em Safari o evento afterprint nem sempre chega, daí o timeout.
   */
  const printAs = (mode: "both" | "steps" | "diagram") => {
    setPrintMenu(null);
    const root = document.documentElement;
    const cls = mode === "steps" ? "print-steps-only" : mode === "diagram" ? "print-diagram-only" : "";
    if (cls) root.classList.add(cls);
    window.print();
    window.setTimeout(() => {
      root.classList.remove("print-steps-only", "print-diagram-only");
    }, 500);
  };

  const load = async () => {
    if (!workflowId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // O nome do responsável vem no próprio detalhe: quem só consulta não alcança o endpoint de
      // perfis, que é da configuração.
      setDetail(await workflowCatalogService.getById(workflowId));
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar o fluxo de trabalho."), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Fluxo de trabalho";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const goToList = () => navigateTo("workflow-catalog");

  const steps = useMemo(
    () => [...(detail?.steps ?? [])].sort((a, b) => a.order - b.order),
    [detail]
  );

  /** Rótulo "N. Nome" por chave, para mostrar o destino das ligações em texto. */
  const labelByKey = useMemo(() => {
    const map = new Map<string, string>();
    steps.forEach((step, index) => map.set(step.stepKey, `${index + 1}. ${step.name}`));
    return map;
  }, [steps]);

  // As ligações vêm por step_key; o diagrama trabalha com uid, e aqui a própria chave serve de uid.
  const transitionsByStep = useMemo(() => {
    const next = new Map<string, string>();
    const byOption = new Map<string, string>();
    const workflowNames: Record<string, string> = {};
    (detail?.transitions ?? []).forEach((transition) => {
      let target = DIAGRAM_END;
      if (transition.toWorkflowId) {
        target = diagramWorkflowTarget(transition.toWorkflowId);
        if (transition.toWorkflowName) workflowNames[transition.toWorkflowId] = transition.toWorkflowName;
      } else if (transition.toStepKey && labelByKey.has(transition.toStepKey)) {
        target = transition.toStepKey;
      }
      if (transition.optionKey) {
        byOption.set(`${transition.fromStepKey}|${transition.optionKey}`, target);
        return;
      }
      next.set(transition.fromStepKey, target);
    });
    return { next, byOption, workflowNames };
  }, [detail, labelByKey]);

  const diagramSteps: WorkflowDiagramStep[] = useMemo(
    () =>
      steps.map((step) => ({
        uid: step.stepKey,
        kind: step.kind,
        name: step.name,
        next: transitionsByStep.next.get(step.stepKey) ?? DIAGRAM_END,
        options: step.options.map((option) => ({
          uid: `${step.stepKey}|${option.optionKey}`,
          label: option.label,
          optionKey: option.optionKey,
          target:
            transitionsByStep.byOption.get(`${step.stepKey}|${option.optionKey}`) ?? DIAGRAM_END,
        })),
      })),
    [steps, transitionsByStep]
  );

  const targetLabel = (key: string): string => {
    const workflowId = diagramWorkflowId(key);
    if (workflowId) {
      const name = transitionsByStep.workflowNames[workflowId];
      // Sem nome resolvido o fluxo saiu do ar; mostrar o id não diria nada a quem lê.
      return name ? `Segue no fluxo: ${name}` : "Segue em outro fluxo (indisponível)";
    }
    return labelByKey.get(key) ?? "Encerra o processo";
  };

  /** A ligação para outro fluxo substitui a frase inteira — não é "a próxima etapa". */
  const nextStepText = (key: string): string =>
    diagramWorkflowId(key) ? targetLabel(key) : `Depois desta etapa: ${targetLabel(key)}`;

  const profileName = (profileId: string | null): string | null =>
    profileId ? detail?.profileNames?.[profileId] ?? null : null;

  const dueLabel = (anchor: string, amount: number | null, unit: string): string | null => {
    if (anchor === "none" || amount === null) return null;
    const anchorLabel = DUE_ANCHOR_LABELS[anchor as keyof typeof DUE_ANCHOR_LABELS] ?? anchor;
    const unitLabel = DUE_UNIT_LABELS[unit as keyof typeof DUE_UNIT_LABELS] ?? unit;
    return `${amount} ${unitLabel} a partir de: ${anchorLabel.toLowerCase()}`;
  };

  const download = async (attachment: WorkflowAttachment) => {
    try {
      const blob = await workflowCatalogService.downloadAttachment(attachment.id);
      downloadBlob(blob, attachment.fileName);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao baixar o documento."), "error");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              display: "flex",
              // Ações ficam na linha do título, inclusive no celular (RN-539h): com "wrap"
              // o bloco do título quebrava antes de encolher e as ações caíam para baixo.
              flexWrap: "nowrap",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
              <IconButton onClick={goToList} title="Voltar para Fluxos de trabalho" sx={{ mt: "-4px" }}>
                <ArrowBack />
              </IconButton>
              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Typography
                    variant="h4"
                    className="c4u-page-header__title"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {detail?.name || "Fluxo de trabalho"}
                  </Typography>
                  {detail && (
                    <>
                      {detail.category && (
                        <Chip
                          label={detail.category}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      <Chip
                        label={TARGET_KIND_LABELS[detail.targetKind] ?? detail.targetKind}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                      <Chip
                        label={`v${detail.versionNumber}`}
                        size="small"
                        sx={{ height: 22, fontSize: "0.7rem", color: "#fff", bgcolor: colors.primary }}
                      />
                    </>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  className="c4u-page-header__subtitle"
                >
                  {detail?.purpose ||
                    "Versão em vigor deste processo. Consulta apenas — quem configura usa o cadastro de fluxos."}
                  {detail?.publishedAt ? ` · Publicada em ${formatDate(detail.publishedAt)}` : ""}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-end",
                // Ações não quebram para a segunda linha: o cabeçalho encolhe, elas não.
                flexShrink: 0,
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
              }}
              className="no-print"
            >
              <Button
                variant="contained"
                startIcon={<Print />}
                endIcon={<ArrowDropDown />}
                onClick={(e) => setPrintMenu(e.currentTarget)}
                disabled={loading || !detail}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#6c757d",
                  "&:hover": { backgroundColor: "#5c636a" },
                }}
              >
                Imprimir
              </Button>
              <Menu
                anchorEl={printMenu}
                open={Boolean(printMenu)}
                onClose={() => setPrintMenu(null)}
              >
                <MenuItem onClick={() => printAs("both")}>Etapas e diagrama</MenuItem>
                <MenuItem onClick={() => printAs("steps")}>Somente as etapas</MenuItem>
                <MenuItem onClick={() => printAs("diagram")}>Somente o diagrama</MenuItem>
              </Menu>
              <FaqButton />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : !detail ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                Fluxo não encontrado
              </Typography>
              <Typography variant="body2">
                Ele pode ter saído do ar ou não estar liberado para o seu perfil.
              </Typography>
            </Box>
          ) : (
            <>
              {detail.videoEmbedUrl && (
                <Paper
                  elevation={0}
                  className="workflow-print-video"
                  sx={{ p: 1.5, mb: 2, border: "1px solid #e9ecef", borderRadius: "8px" }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                    Vídeo de apresentação
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 760,
                      aspectRatio: "16 / 9",
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#000",
                    }}
                  >
                    <iframe
                      src={detail.videoEmbedUrl}
                      title={`Vídeo de apresentação do fluxo ${detail.name}`}
                      allowFullScreen
                      // O YouTube valida a origem pelo Referer: com no-referrer o player recusa
                      // com "erro 153". strict-origin manda só o domínio, sem caminho nem query.
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                      style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                    />
                  </Box>
                </Paper>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  className="workflow-print-steps"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {steps.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 6,
                        color: colors.textSecondary,
                        border: "1px dashed #ced4da",
                        borderRadius: "8px",
                      }}
                    >
                      <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                      <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                        Nenhuma etapa neste fluxo
                      </Typography>
                      <Typography variant="body2">
                        A versão publicada não tem etapas desenhadas.
                      </Typography>
                    </Box>
                  ) : (
                    steps.map((step, index) => {
                      const responsible = profileName(step.responsibleProfileId);
                      const due = dueLabel(step.dueAnchor, step.dueAmount, step.dueUnit);
                      return (
                        <Paper
                          key={step.stepKey}
                          elevation={0}
                          className="workflow-print-step"
                          sx={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            p: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Chip
                              label={`Etapa ${index + 1}`}
                              size="small"
                              sx={{ bgcolor: colors.primary, color: "white", fontWeight: 600 }}
                            />
                            <Chip
                              label={STEP_KIND_LABELS[step.kind] ?? step.kind}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.7rem" }}
                            />
                            {!step.required && (
                              <Chip
                                label="Opcional"
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.7rem" }}
                              />
                            )}
                          </Box>

                          <Typography
                            sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word" }}
                          >
                            {step.name}
                          </Typography>

                          {step.description && (
                            <Typography
                              variant="body2"
                              sx={{ color: colors.textSecondary, whiteSpace: "pre-line", wordBreak: "break-word" }}
                            >
                              {step.description}
                            </Typography>
                          )}

                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                              <Typography variant="caption" sx={{ display: "block", color: colors.textMuted }}>
                                Prazo
                              </Typography>
                              <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                                {due ?? "Sem prazo"}
                              </Typography>
                            </Box>
                            {responsible && (
                              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                                <Typography variant="caption" sx={{ display: "block", color: colors.textMuted }}>
                                  Responsável
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: colors.textPrimary, wordBreak: "break-word" }}
                                >
                                  {responsible}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {step.kind === "decision" ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                Opções da decisão
                              </Typography>
                              {step.options.map((option) => (
                                <Box
                                  key={option.optionKey}
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "baseline",
                                    gap: 0.5,
                                    p: 0.75,
                                    border: "1px solid #e9ecef",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word" }}
                                  >
                                    {option.label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: colors.textSecondary, minWidth: 0, wordBreak: "break-word" }}
                                  >
                                    →{" "}
                                    {targetLabel(
                                      transitionsByStep.byOption.get(
                                        `${step.stepKey}|${option.optionKey}`
                                      ) ?? DIAGRAM_END
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: colors.textSecondary, wordBreak: "break-word" }}
                            >
                              {nextStepText(transitionsByStep.next.get(step.stepKey) ?? DIAGRAM_END)}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })
                  )}

                  <Paper
                    elevation={0}
                    className="workflow-print-block workflow-print-attachments"
                    sx={{
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                      Documentos de apoio
                    </Typography>
                    {detail.attachments.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 3,
                          color: colors.textSecondary,
                          border: "1px dashed #ced4da",
                          borderRadius: "8px",
                        }}
                      >
                        <InsertDriveFile sx={{ fontSize: 36, color: "#ced4da", mb: 0.5 }} />
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          Nenhum documento neste fluxo
                        </Typography>
                        <Typography variant="caption">
                          Modelos e checklists aparecem aqui quando forem anexados ao processo.
                        </Typography>
                      </Box>
                    ) : (
                      detail.attachments.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                        >
                          <InsertDriveFile sx={{ color: colors.textSecondary, flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{ color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}
                            >
                              {attachment.fileName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {formatFileSize(attachment.sizeBytes)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => download(attachment)}
                            title="Baixar"
                            sx={{ color: colors.primary, flexShrink: 0 }}
                          >
                            <Download sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Box>
                      ))
                    )}
                  </Paper>
                </Box>

                <Paper
                  elevation={0}
                  className="workflow-print-diagram workflow-print-block"
                  sx={{
                    width: { xs: "100%", lg: 560, xl: 640 },
                    flexShrink: 0,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    p: 1.5,
                    // Gruda no topo e rola por dentro: antes o diagrama só começava a rolar depois
                    // que as etapas terminavam, e um fluxo longo deixava o desenho preso fora da tela.
                    position: { lg: "sticky" },
                    top: { lg: "100px" },
                    maxHeight: { lg: "calc(100vh - 120px)" },
                    overflowY: { lg: "auto" },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                    Diagrama
                  </Typography>
                  <WorkflowDiagram
                    steps={diagramSteps}
                    workflowNames={transitionsByStep.workflowNames}
                    showLegend
                    expandable
                    title={detail.name}
                    defaultExpanded={getParam("diagram") === "full"}
                    buildWorkflowUrl={(id, fullscreen) =>
                      `?page=workflow-view&workflowId=${id}${fullscreen ? "&diagram=full" : ""}`
                    }
                    onPrint={() => printAs("diagram")}
                    emptyMessage="Sem etapas para desenhar"
                    emptyHint="Esta versão foi publicada sem etapas."
                  />
                </Paper>
              </Box>
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />

      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 portrait; margin: 10mm; }
          /* O que rola na tela precisa aparecer inteiro no papel. */
          .workflow-print-diagram {
            overflow: visible !important;
            position: static !important;
            max-height: none !important;
          }
          /* Tamanho natural no papel: esticar para a largura da folha engorda os nós sem ganhar
             legibilidade. O desenho fica centrado, e o que passar da folha é cortado pelo navegador. */
          .workflow-print-diagram svg { width: auto !important; max-width: 100% !important; }
          /* No papel o desenho fica centrado na folha, como na tela. */
          .workflow-diagram-scroll { justify-content: center !important; overflow: visible !important; }
          /* A folha começa no conteúdo: margem e espaçamento herdados da tela empurravam o
             desenho para a página seguinte, deixando a primeira em branco. */
          .workflow-print-diagram { margin: 0 !important; padding: 0 !important; border: 0 !important; }
          .workflow-print-diagram > *:first-of-type { margin-top: 0 !important; }
          .workflow-diagram-scroll { padding-bottom: 0 !important; }
          .print-diagram-only main > * { margin-top: 0 !important; }
          /* Player não imprime: o iframe vira um retângulo cinza ocupando a folha. O vídeo fica
             fora do papel em qualquer modo. */
          .workflow-print-video { display: none !important; }
          /* Impressão seletiva: a escolha do menu marca o <html> antes de chamar o print. */
          .print-steps-only .workflow-print-diagram { display: none !important; }
          .print-diagram-only .workflow-print-steps,
          .print-diagram-only .workflow-print-attachments { display: none !important; }
          .print-diagram-only .workflow-print-diagram { width: 100% !important; }
          /* Etapa é uma unidade de leitura: não parte entre páginas. */
          .workflow-print-step { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; }
          .workflow-print-block { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </Box>
  );
};

export default WorkflowView;
